import { useLanguage } from '@/context/LanguageContext';
import { MapPin, Shield, TreePine, Waves, Trophy, GraduationCap, Car } from 'lucide-react';

const AREA_IMAGES = [
  '/project/hacienda-1.webp',
  '/project/hacienda-2.webp'
];

const FEATURE_ICONS = [Shield, TreePine, Trophy, Waves, GraduationCap, Car];

export default function LocationSection() {
  const { t } = useLanguage();
  const features = t('marbella.features') || [];

  return (
    <>
      {/* Map Section */}
      <section id="location" className="py-24 md:py-32 lg:py-40 bg-[#0f1115]" data-testid="location-section">
        <div className="px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            <div className="space-y-6">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C8B598]">
                {t('location.overline')}
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0] leading-tight">
                {t('location.title')}
              </h2>
              <p className="text-base font-light leading-relaxed text-[#9CA3AF]">
                {t('location.description')}
              </p>
              <div className="flex items-start gap-3 pt-4">
                <MapPin className="w-5 h-5 text-[#C8B598] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#F5F5F0] font-light">{t('location.address')}</p>
              </div>
              <a
                href="https://maps.app.goo.gl/UY6zGv7N3EvUm4nK8"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#C8B598] hover:text-[#E2D1B8] transition-colors pt-2"
                data-testid="google-maps-link"
              >
                <MapPin className="w-3.5 h-3.5" />
                Google Maps
              </a>
            </div>

            <div className="relative min-h-[350px] md:min-h-[450px] border border-white/5 overflow-hidden" data-testid="map-container">
              <iframe
                title="Location Map"
                src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d1600!2d-4.750880!3d36.502649!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMzbCsDMwJzA5LjUiTiA0wrA0NScwMy4yIlc!5e1!3m2!1sen!2ses!4v1700000000000"
                className="absolute inset-0 w-full h-full border-0 grayscale contrast-125 opacity-80"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Marbella Lifestyle Section */}
      <section className="py-24 md:py-32 lg:py-40 bg-[#050505] relative overflow-hidden" data-testid="marbella-section">
        {/* Background image */}
        <div className="absolute inset-0 opacity-10">
          <img
            src={AREA_IMAGES[0]}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        <div className="relative z-10 px-6 md:px-12 lg:px-24 max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 mb-16">
            <div className="space-y-6">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C8B598]">
                {t('marbella.overline')}
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F5F5F0] leading-tight">
                {t('marbella.title')}
              </h2>
              <p className="font-heading text-xl sm:text-2xl font-light text-[#C8B598] italic">
                {t('marbella.subtitle')}
              </p>
              <p className="text-base font-light leading-relaxed text-[#9CA3AF]">
                {t('marbella.description')}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Array.isArray(features) && features.map((feature, i) => {
                const Icon = FEATURE_ICONS[i] || Sun;
                return (
                  <div
                    key={i}
                    className="flex items-start gap-3 p-4 bg-[#0f1115]/60 backdrop-blur border border-white/5 hover:border-[#C8B598]/20 transition-colors"
                    data-testid={`marbella-feature-${i}`}
                  >
                    <Icon className="w-4 h-4 text-[#C8B598] flex-shrink-0 mt-0.5" />
                    <span className="text-sm font-light text-[#F5F5F0]">{feature}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Area Images */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="marbella-images">
            {AREA_IMAGES.map((img, i) => (
              <div key={i} className="overflow-hidden group">
                <img
                  src={img}
                  alt={`Hacienda Las Chapas ${i + 1}`}
                  className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-[1.5s] ease-out"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
